// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useState } from 'react';
import { Form, Input, Button, Card, Row, Col, Switch, Typography, Select, Modal, Checkbox, TimePicker, Avatar, Alert } from 'antd';
import { Clock, ChevronDown, ChevronRight, Lightbulb, Plus, Search, User } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import styles from './SystemSettingsPage.module.css';
import { buildFullUrl } from '../contexts/AvatarContext';
import { useSystemSettings } from '@/features/settings/useSystemSettings';
// Re-export types để các component khác có thể sử dụng
export type { 
  CompanyInfo, 
  WorkShift, 
  MealAllowanceConfig, 
  Employee, 
  DepartmentWithEmployees, 
  GPSPoint 
} from '@/features/settings/useSystemSettings';

const { Text } = Typography;
const { Option } = Select;

// Custom Time Picker Component
interface CustomTimePickerProps {
  value?: string; // Format: "HH:mm"
  onChange?: (value: string | undefined) => void;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  style?: React.CSSProperties;
}

const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  size = 'large',
  className,
  style,
}) => {
  const [open, setOpen] = useState(false);

  // Chuẩn hoá string "HH:mm" cho TimePicker
  const normalizeTimeForPicker = (time?: string): string | undefined => {
    if (!time) return undefined;
    const trimmed = time.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(/\s*:\s*/g, ':');
    const parts = normalized.split(':');
    if (parts.length < 2) return undefined;
    const hh = parts[0]?.trim();
    const mm = parts[1]?.trim();
    if (!hh || !mm) return undefined;
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
  };

  // Map string "HH:mm" <-> Dayjs cho TimePicker (giống popup Cập nhật chấm công)
  const toDayjs = (time?: string): Dayjs | null => {
    const normalized = normalizeTimeForPicker(time);
    if (!normalized) return null;
    return dayjs(normalized, 'HH:mm');
  };

  const fromDayjs = (val: Dayjs | null): string | undefined => {
    if (!val) return undefined;
    return val.format('HH:mm');
  };

  return (
    <TimePicker
      value={toDayjs(value)}
      onChange={(val) => {
        const newValueStr = fromDayjs(val);
        onChange?.(newValueStr);
      }}
      onCalendarChange={(val) => {
        // Lưu giá trị ngay khi click chọn (giống Cập nhật chấm công)
        if (val && !Array.isArray(val)) {
          const newValueStr = fromDayjs(val);
          onChange?.(newValueStr);
          // Tự động đóng popup sau 100ms
          setTimeout(() => {
            setOpen(false);
          }, 100);
        }
      }}
      open={open}
      onOpenChange={setOpen}
      size={size}
      format="HH:mm"
      placeholder="--:--"
      className={className}
      style={style}
      suffixIcon={<Clock size={16} style={{ color: '#8c8c8c' }} />}
      minuteStep={1}
      renderExtraFooter={() => null}
    />
  );
};



export const SystemSettingsPage: React.FC = () => {
  // Gọi hook domain để lấy tất cả logic và state
  const {
    form,
    loading,
    initialLoading,
    error,
    companyInfoChanged,
    workShifts,
    workShiftsChanged,
    expandedShifts,
    shiftErrors,
    initialMealAllowance,
    mealAllowanceChanged,
    setHasUserInteracted,
    gpsPoints,
    gpsPointsChanged,
    expandedGpsPoints,
    expandedSections,
    applicableModalVisible,
    searchText,
    setSearchText,
    selectedEmployees,
    departmentsWithEmployees,
    loadingDepartments,
    expandedDepartments,
    avatarCacheRef,
    fetchAvatarAsDataUri,
    avatarRefreshKey,
    setHasCompanyInteracted,
    handleCompanyInfoSave,
    handleCompanyInfoCancel,
    toggleShift,
    handleShiftToggle,
    handleShiftTimeChange,
    handleStandardHoursChange,
    updateWorkShift,
    addWorkShift,
    handleWorkShiftsSubmit,
    handleWorkShiftsCancel,
    handleMealAllowanceSave,
    handleMealAllowanceCancel,
    toggleGpsPoint,
    handleGpsPointToggle,
    addGpsPoint,
    updateGpsPoint,
    handleGpsConfigSave,
    handleGpsConfigCancel,
    toggleSection,
    openApplicableModal,
    closeApplicableModal,
    toggleDepartment,
    handleEmployeeToggle,
    handleDepartmentToggle,
    handleSelectAll,
    handleSaveApplicableSelection,
    getFilteredDepartments,
    getApplicableScopeDisplay,
    getWorkShiftApplicableDisplay,
  } = useSystemSettings();


  // Component để render avatar với auth (giống PersonnelManagementPage)
  interface EmployeeAvatarProps {
    avatarUrl: string | null | undefined;
    fetchAvatarAsDataUri: (url: string) => Promise<string | null>;
    cacheRef: React.MutableRefObject<Map<string, string>>;
  }

  const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({ avatarUrl, fetchAvatarAsDataUri, cacheRef }) => {
    const [avatarDataUri, setAvatarDataUri] = React.useState<string | null>(null);
    const [avatarError, setAvatarError] = React.useState(false);
    const [useDirectUrl, setUseDirectUrl] = React.useState(false);

    React.useEffect(() => {
      // Reset state khi avatarUrl thay đổi
      setAvatarDataUri(null);
      setAvatarError(false);
      setUseDirectUrl(false);

      if (!avatarUrl) {
        return;
      }

      const isValidUrl = avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://');
      if (!isValidUrl) {
        return;
      }

      // Check cache first
      if (cacheRef.current.has(avatarUrl)) {
        const cached = cacheRef.current.get(avatarUrl);
        if (cached) {
          setAvatarDataUri(cached);
          setAvatarError(false);
          return;
        }
      }

      // Fetch avatar với error handling
      fetchAvatarAsDataUri(avatarUrl)
        .then((dataUri) => {
          if (dataUri) {
            // Cache the result
            cacheRef.current.set(avatarUrl, dataUri);
            setAvatarDataUri(dataUri);
            setAvatarError(false);
            setUseDirectUrl(false);
          } else {
            // Fallback: dùng URL trực tiếp nếu fetch data URI thất bại
            setUseDirectUrl(true);
            setAvatarError(false);
          }
        })
        .catch((error) => {
          console.error('Error fetching avatar:', error);
          // Fallback: dùng URL trực tiếp
          setUseDirectUrl(true);
          setAvatarError(false);
        });
    }, [avatarUrl, fetchAvatarAsDataUri, cacheRef]);

    // Sử dụng data URI nếu có, nếu không thì dùng URL trực tiếp (fallback)
    const avatarSrc = avatarDataUri && !avatarError ? avatarDataUri : (useDirectUrl && avatarUrl ? avatarUrl : undefined);
    const showAvatar = !!avatarSrc;

    return (
      <Avatar 
        size={32} 
        src={showAvatar ? avatarSrc : undefined}
        icon={!showAvatar ? <User size={18} /> : undefined}
        onError={() => {
          // Nếu đang dùng data URI mà lỗi, thử fallback về URL trực tiếp
          if (avatarDataUri && !useDirectUrl) {
            setUseDirectUrl(true);
            setAvatarDataUri(null);
            setAvatarError(false);
          } else {
            setAvatarError(true);
          }
          return false;
        }}
      />
    );
  };
  

  if (initialLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography.Text>Đang tải dữ liệu...</Typography.Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Hiển thị error message nếu có lỗi */}
        {error && (
          <Alert
            message="Lỗi"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical" initialValues={{
          companyInfo: {
            companyName: '',
            address: '',
            taxCode: '',
            phoneNumber: '',
          },
          mealAllowance: initialMealAllowance,
        }}>
          {/* Company Information Section */}
          <Card className={styles.sectionCard}>
            <div 
              className={styles.sectionHeader}
              onClick={() => toggleSection('company')}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <h3 className={styles.sectionTitle}>Thông tin Doanh nghiệp</h3>
                <Text className={styles.sectionDescription}>
                  Cập nhật thông tin công ty hiển thị trên báo cáo
                </Text>
              </div>
              {expandedSections.has('company') ? (
                <ChevronDown size={20} className={styles.sectionChevron} />
              ) : (
                <ChevronRight size={20} className={styles.sectionChevron} />
              )}
            </div>
            {expandedSections.has('company') && (
              <>
                <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['companyInfo', 'companyName']} label="Tên công ty">
                  <Input
                    size="large"
                    className={styles.input}
                    onChange={(e) => {
                      form.setFieldsValue({
                        companyInfo: {
                          ...form.getFieldValue('companyInfo'),
                          companyName: e.target.value,
                        },
                      });
                      setHasCompanyInteracted(true);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['companyInfo', 'taxCode']} label="Mã số thuế">
                  <Input
                    size="large"
                    className={styles.input}
                    onChange={(e) => {
                      form.setFieldsValue({
                        companyInfo: {
                          ...form.getFieldValue('companyInfo'),
                          taxCode: e.target.value,
                        },
                      });
                      setHasCompanyInteracted(true);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['companyInfo', 'address']} label="Địa chỉ">
                  <Input
                    size="large"
                    className={styles.input}
                    onChange={(e) => {
                      form.setFieldsValue({
                        companyInfo: {
                          ...form.getFieldValue('companyInfo'),
                          address: e.target.value,
                        },
                      });
                      setHasCompanyInteracted(true);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['companyInfo', 'phoneNumber']} label="Số điện thoại">
                  <Input
                    size="large"
                    className={styles.input}
                    onChange={(e) => {
                      form.setFieldsValue({
                        companyInfo: {
                          ...form.getFieldValue('companyInfo'),
                          phoneNumber: e.target.value,
                        },
                      });
                      setHasCompanyInteracted(true);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            {companyInfoChanged && (
              <div className={styles.mealAllowanceActions}>
                <Button size="large" onClick={handleCompanyInfoCancel} className={styles.cancelButton}>
                  Huỷ bỏ
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleCompanyInfoSave}
                  loading={loading}
                  className={styles.saveButton}
                >
                  Lưu cài đặt
                </Button>
              </div>
            )}
              </>
            )}
          </Card>

          {/* Work Shift Configuration Section */}
          <Card className={styles.sectionCard}>
            <div 
              className={styles.sectionHeader}
              onClick={() => toggleSection('workShift')}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <h3 className={styles.sectionTitle}>Cấu Hình Ca Làm Việc</h3>
                <Text className={styles.sectionDescription}>
                  Thiết lập thông tin giờ vào, giờ ra, giờ nghỉ trưa cho từng ca
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {expandedSections.has('workShift') && (
                  <Button
                    size="large"
                    icon={<Plus size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      addWorkShift();
                    }}
                    className={styles.cancelButton}
                  >
                    Thêm ca làm việc
                  </Button>
                )}
                {expandedSections.has('workShift') ? (
                  <ChevronDown size={20} className={styles.sectionChevron} />
                ) : (
                  <ChevronRight size={20} className={styles.sectionChevron} />
                )}
              </div>
            </div>
            {expandedSections.has('workShift') && (
              <>
                {workShifts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">Chưa có ca làm việc nào. Vui lòng thêm ca mới.</Text>
                  </div>
                ) : (
                  <div className={styles.shiftsList}>
                    {workShifts.map((shift) => {
                const isExpanded = expandedShifts.has(shift.id);
                return (
                  <div key={shift.id} className={styles.shiftItem}>
                    <div className={`${styles.shiftHeader} ${isExpanded ? styles.shiftHeaderExpanded : ''}`}>
                      <div
                        className={styles.shiftToggle}
                        onClick={() => toggleShift(shift.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown size={20} className={styles.chevronIcon} />
                        ) : (
                          <ChevronRight size={20} className={styles.chevronIcon} />
                        )}
                        <span className={styles.shiftName}>{shift.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Switch
                          checked={shift.enabled}
                          onChange={(checked) => handleShiftToggle(shift.id, checked)}
                          className={styles.shiftSwitch}
                        />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className={styles.shiftContent}>
                        <Form.Item
                          label="Tên ca *"
                          validateStatus={shiftErrors[shift.id]?.name ? 'error' : undefined}
                          help={shiftErrors[shift.id]?.name}
                        >
                          <Input
                            size="large"
                            className={styles.input}
                            value={shift.name}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Update shift name và validate
                              handleShiftTimeChange(shift.id, 'name', val);
                            }}
                            placeholder="Nhập tên ca làm việc"
                          />
                        </Form.Item>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                                label="Giờ vào *"
                                validateStatus={shiftErrors[shift.id]?.checkInTime ? 'error' : undefined}
                                help={shiftErrors[shift.id]?.checkInTime}
                              >
                                <CustomTimePicker
                                  size="large"
                                  className={styles.timeInput}
                                  value={shift.checkInTime}
                                  onChange={(value) => {
                                    handleShiftTimeChange(shift.id, 'checkInTime', value);
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                                label="Giờ ra *"
                                validateStatus={shiftErrors[shift.id]?.checkOutTime ? 'error' : undefined}
                                help={shiftErrors[shift.id]?.checkOutTime}
                              >
                                <CustomTimePicker
                                  size="large"
                                  className={styles.timeInput}
                                  value={shift.checkOutTime}
                                  onChange={(value) => {
                                    handleShiftTimeChange(shift.id, 'checkOutTime', value);
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                                label="Nghỉ trưa (Bắt đầu)"
                                validateStatus={shiftErrors[shift.id]?.lunchBreakStart ? 'error' : undefined}
                                help={shiftErrors[shift.id]?.lunchBreakStart}
                              >
                                <CustomTimePicker
                                  size="large"
                                  className={styles.timeInput}
                                  value={shift.lunchBreakStart}
                                  onChange={(value) => {
                                    handleShiftTimeChange(shift.id, 'lunchBreakStart', value);
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                                label="Nghỉ trưa (Kết thúc)"
                                validateStatus={shiftErrors[shift.id]?.lunchBreakEnd ? 'error' : undefined}
                                help={shiftErrors[shift.id]?.lunchBreakEnd}
                              >
                                <CustomTimePicker
                                  size="large"
                                  className={styles.timeInput}
                                  value={shift.lunchBreakEnd}
                                  onChange={(value) => {
                                    handleShiftTimeChange(shift.id, 'lunchBreakEnd', value);
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item label="Tổng giờ công chuẩn">
                              <Input
                                type="number"
                                size="large"
                                className={styles.input}
                                value={shift.standardWorkingHours ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? null : parseFloat(e.target.value);
                                  handleStandardHoursChange(shift.id, value);
                                }}
                                min={0}
                                max={24}
                                step="0.01"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Đối tượng áp dụng cho ca này">
                              <Select
                                size="large"
                                className={styles.select}
                                value={shift.applyToAllEmployees === false ? 'specific' : 'all'}
                                onChange={(value) => {
                                  // Update work shift applyToAllEmployees
                                  updateWorkShift(shift.id, {
                                    applyToAllEmployees: value === 'all',
                                    applicableUserIds: value === 'specific' ? shift.applicableUserIds || [] : [],
                                    applicableDepartmentIds: value === 'specific' ? shift.applicableDepartmentIds || [] : [],
                                  });
                                  if (value === 'specific') {
                                    const selectedIds = (shift.applicableUserIds || []).map(String);
                                    setTimeout(() => {
                                      openApplicableModal(`ws:${shift.id}`, selectedIds);
                                    }, 0);
                                  } else {
                                    if (applicableModalVisible === `ws:${shift.id}`) {
                                      closeApplicableModal();
                                    }
                                  }
                                }}
                                suffixIcon={<ChevronDown size={16} />}
                              >
                                <Option value="all">Tất cả đối tượng</Option>
                                <Option value="specific">Theo đối tượng cụ thể</Option>
                              </Select>
                              {shift.applyToAllEmployees === false && (
                                <Button
                                  type="link"
                                  onClick={() =>
                                    openApplicableModal(`ws:${shift.id}`, (shift.applicableUserIds || []).map(String))
                                  }
                                  style={{ padding: 0, marginTop: 8 }}
                                >
                                  {getWorkShiftApplicableDisplay(shift)}
                                </Button>
                              )}
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                );
              })}
                  </div>
                )}
            {workShiftsChanged && (
              <div className={styles.shiftActions}>
                <Button size="large" onClick={handleWorkShiftsCancel} className={styles.cancelButton}>
                  Huỷ bỏ
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleWorkShiftsSubmit}
                  loading={loading}
                  className={styles.saveButton}
                >
                  Lưu cài đặt
                </Button>
              </div>
            )}
              </>
            )}
          </Card>

          {/* Meal Allowance Configuration Section */}
          <Card className={styles.sectionCard}>
            <div 
              className={styles.sectionHeader}
              onClick={() => toggleSection('mealAllowance')}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <h3 className={styles.sectionTitle}>Cấu hình phụ cấp cơm</h3>
                <Text className={styles.sectionDescription}>
                  Tính toán phụ cấp dựa trên số giờ làm tối thiểu
                </Text>
              </div>
              {expandedSections.has('mealAllowance') ? (
                <ChevronDown size={20} className={styles.sectionChevron} />
              ) : (
                <ChevronRight size={20} className={styles.sectionChevron} />
              )}
            </div>
            {expandedSections.has('mealAllowance') && (
              <>
                <Form.Item
              name={['mealAllowance', 'minimumHours']}
              label="Số giờ tối thiểu để tính cơm"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập số giờ tối thiểu',
                },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const numValue = Number(value);
                    if (isNaN(numValue)) {
                      return Promise.reject(new Error('Số giờ tối thiểu phải là số'));
                    }
                    if (numValue < 0) {
                      return Promise.reject(new Error('Số giờ tối thiểu phải >= 0'));
                    }
                    if (numValue > 24) {
                      return Promise.reject(new Error('Số giờ tối thiểu phải <= 24'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              normalize={(value) => {
                if (value === undefined || value === null || value === '') {
                  return value;
                }
                // Chấp nhận cả số nguyên và số thập phân, tự động chuyển đổi thành number
                const numValue = Number(value);
                return isNaN(numValue) ? value : numValue;
              }}
            >
              <Input
                type="number"
                size="large"
                className={styles.input}
                min={0}
                max={24}
                step="any"
                onFocus={() => setHasUserInteracted(true)}
              />
            </Form.Item>
            <Text className={styles.helperText}>
              Nhân viên phải làm tối thiểu số giờ được tính phụ cấp cơm
            </Text>
            <div className={styles.noteSection}>
              <div className={styles.noteHeader}>
                <Lightbulb size={16} className={styles.noteIcon} />
                <Text strong className={styles.noteTitle}>Lưu ý</Text>
              </div>
              <Text className={styles.noteContent}>
                Phụ cấp công sẽ được tính dựa trên số giờ làm thực tế so với quy định. Nếu nhân viên làm dưới số giờ tối thiểu, sẽ không được tính phụ cấp.
              </Text>
            </div>
            {mealAllowanceChanged && (
              <div className={styles.mealAllowanceActions}>
                <Button size="large" onClick={handleMealAllowanceCancel} className={styles.cancelButton}>
                  Huỷ bỏ
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleMealAllowanceSave}
                  loading={loading}
                  className={styles.saveButton}
                >
                  Lưu cài đặt
                </Button>
              </div>
            )}
              </>
            )}
          </Card>

          {/* GPS Timekeeping Configuration Section */}
          <Card className={styles.sectionCard}>
            <div 
              className={styles.sectionHeader}
              onClick={() => toggleSection('gps')}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <h3 className={styles.sectionTitle}>Cấu hình GPS chấm công</h3>
                <Text className={styles.sectionDescription}>
                  Thiết lập vị trí, bán kính và quy định chấm công theo GPS
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {expandedSections.has('gps') && (
                  <Button
                    size="large"
                    icon={<Plus size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      addGpsPoint();
                    }}
                    className={styles.cancelButton}
                  >
                    Thêm điểm
                  </Button>
                )}
                {expandedSections.has('gps') ? (
                  <ChevronDown size={20} className={styles.sectionChevron} />
                ) : (
                  <ChevronRight size={20} className={styles.sectionChevron} />
                )}
              </div>
            </div>
            {expandedSections.has('gps') && (
              <div className={styles.gpsPointsContainer}>
                {gpsPoints.map((point, index) => {
                  const isExpanded = expandedGpsPoints.has(point.id);
                  return (
                    <div key={point.id} className={styles.gpsPointCard}>
                      <div className={`${styles.gpsPointHeader} ${isExpanded ? styles.gpsPointHeaderExpanded : ''}`}>
                        <div
                          className={styles.shiftToggle}
                          onClick={() => toggleGpsPoint(point.id)}
                          style={{ flex: 1 }}
                        >
                          {isExpanded ? (
                            <ChevronDown size={20} className={styles.chevronIcon} />
                          ) : (
                            <ChevronRight size={20} className={styles.chevronIcon} />
                          )}
                          <Text strong className={styles.gpsPointTitle}>
                            {point.locationName || `Điểm #${index + 1}`}
                          </Text>
                        </div>
                        <div 
                          style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            checked={point.enabled}
                            onChange={(checked) => handleGpsPointToggle(point.id, checked)}
                            className={styles.shiftSwitch}
                          />
                        </div>
                      </div>
                      {isExpanded && (
                        <div className={styles.shiftContent}>
                          <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Tên địa điểm">
                          <Input
                            size="large"
                            className={styles.input}
                            value={point.locationName}
                            onChange={(e) => updateGpsPoint(point.id, 'locationName', e.target.value)}
                            placeholder="Nhập tên địa điểm"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Địa chỉ">
                          <Input
                            size="large"
                            className={styles.input}
                            value={point.address}
                            onChange={(e) => updateGpsPoint(point.id, 'address', e.target.value)}
                            placeholder="Nhập địa chỉ"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Vĩ độ">
                          <Input
                            size="large"
                            className={styles.input}
                            value={point.latitude}
                            onChange={(e) => updateGpsPoint(point.id, 'latitude', e.target.value)}
                            placeholder="10.733413706336925"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Kinh độ">
                          <Input
                            size="large"
                            className={styles.input}
                            value={point.longitude}
                            onChange={(e) => updateGpsPoint(point.id, 'longitude', e.target.value)}
                            placeholder="106.69006671124046"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Bán kính (m)">
                          <Input
                            type="number"
                            size="large"
                            className={styles.input}
                            value={point.radius}
                            onChange={(e) => updateGpsPoint(point.id, 'radius', parseInt(e.target.value) || 0)}
                            min={0}
                            placeholder="100"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Đối tượng áp dụng cho điểm này">
                          <Select
                            size="large"
                            className={styles.select}
                            value={point.applicableScope}
                            onChange={(value) => {
                              // Update GPS point applicableScope
                              updateGpsPoint(point.id, 'applicableScope', value);
                              if (value === 'specific') {
                                updateGpsPoint(point.id, 'selectedEmployeeIds', point.selectedEmployeeIds || []);
                                setTimeout(() => {
                                  openApplicableModal(`gps:${point.id}`, point.selectedEmployeeIds || []);
                                }, 0);
                              } else {
                                updateGpsPoint(point.id, 'selectedEmployeeIds', []);
                                if (applicableModalVisible === `gps:${point.id}`) {
                                  closeApplicableModal();
                                }
                              }
                            }}
                            suffixIcon={<ChevronDown size={16} />}
                          >
                            <Option value="all">Tất cả đối tượng</Option>
                            <Option value="specific">Theo đối tượng cụ thể</Option>
                          </Select>
                          {point.applicableScope === 'specific' && (
                            <Button
                              type="link"
                              onClick={() => openApplicableModal(`gps:${point.id}`, point.selectedEmployeeIds || [])}
                              style={{ padding: 0, marginTop: 8 }}
                            >
                              {getApplicableScopeDisplay(point)}
                            </Button>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                          <Form.Item label="Cho phép ngoài bán kính">
                            <Switch
                              checked={point.allowOutsideRadius}
                              onChange={(checked) => updateGpsPoint(point.id, 'allowOutsideRadius', checked)}
                            />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {expandedSections.has('gps') && gpsPointsChanged && (
              <div className={styles.mealAllowanceActions}>
                <Button size="large" onClick={handleGpsConfigCancel} className={styles.cancelButton}>
                  Huỷ bỏ
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleGpsConfigSave}
                  loading={loading}
                  className={styles.saveButton}
                >
                  Lưu cài đặt
                </Button>
              </div>
            )}
          </Card>
        </Form>

        {/* Applicable Subjects Modal */}
        {applicableModalVisible && (
          <Modal
            title={
              applicableModalVisible.startsWith('ws:')
                ? 'Đối tượng áp dụng cho ca này'
                : 'Đối tượng áp dụng cho điểm này'
            }
            open={true}
            onCancel={closeApplicableModal}
            footer={[
              <Button key="cancel" onClick={closeApplicableModal}>
                Hủy
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={() => handleSaveApplicableSelection(applicableModalVisible)}
              >
                Lưu
              </Button>,
            ]}
            width={600}
            className={styles.applicableModal}
          >
            <div className={styles.applicableModalContent}>
              <div className={styles.applicableSearchSection}>
                <Input
                  size="large"
                  placeholder="Tìm kiếm theo tên phòng ban, tên NNV, mã NV, ..."
                  prefix={<Search size={16} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className={styles.applicableSearchInput}
                />
              </div>

              <div className={styles.applicableListContainer}>
                <div className={styles.applicableSelectAllContainer}>
                  <Button
                    type="link"
                    onClick={() => handleSelectAll(applicableModalVisible)}
                    className={styles.selectAllButton}
                    disabled={loadingDepartments || departmentsWithEmployees.length === 0}
                  >
                    Chọn tất cả
                  </Button>
                </div>
                
                {loadingDepartments ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">Đang tải dữ liệu...</Text>
                  </div>
                ) : getFilteredDepartments().length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">Không có dữ liệu</Text>
                  </div>
                ) : (
                  getFilteredDepartments().map((department) => {
                  const isExpanded = expandedDepartments.has(String(department.id));
                  const currentSelection = selectedEmployees[applicableModalVisible] || [];
                  const deptEmployeeIds = department.employees.map((emp) => String(emp.id));
                  const allDeptSelected = deptEmployeeIds.length > 0 && deptEmployeeIds.every((id) => currentSelection.includes(id));
                  const someDeptSelected = deptEmployeeIds.some((id) => currentSelection.includes(id));

                  return (
                    <div key={department.id} className={styles.applicableDepartmentItem}>
                      <div className={styles.applicableDepartmentHeader}>
                        <div
                          className={styles.applicableDepartmentToggle}
                          onClick={() => toggleDepartment(String(department.id))}
                        >
                          {isExpanded ? (
                            <ChevronDown size={16} className={styles.applicableChevron} />
                          ) : (
                            <ChevronRight size={16} className={styles.applicableChevron} />
                          )}
                          <Text strong className={styles.applicableDepartmentName}>
                            {department.name}
                          </Text>
                        </div>
                        <Checkbox
                          checked={allDeptSelected}
                          indeterminate={someDeptSelected && !allDeptSelected}
                          onChange={() => handleDepartmentToggle(applicableModalVisible, department)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {isExpanded && (
                        <div className={styles.applicableEmployeesList}>
                          {department.employees.map((employee) => {
                            const avatarUrl = buildFullUrl(employee.avatar);
                            return (
                              <div key={employee.id} className={styles.applicableEmployeeItem}>
                                <div className={styles.applicableEmployeeInfo}>
                                  <div className={styles.applicableEmployeeAvatar}>
                                    <EmployeeAvatar 
                                      key={`${employee.id}-${avatarRefreshKey}`}
                                      avatarUrl={avatarUrl}
                                      fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                                      cacheRef={avatarCacheRef}
                                    />
                                  </div>
                                  <div className={styles.applicableEmployeeDetails}>
                                    <Text className={styles.applicableEmployeeName}>
                                      {employee.name}
                                    </Text>
                                    <Text type="secondary" className={styles.applicableEmployeeCode}>
                                      {employee.employeeCode}
                                    </Text>
                                  </div>
                                </div>
                                <Checkbox
                                  checked={currentSelection.includes(String(employee.id))}
                                  onChange={() => handleEmployeeToggle(applicableModalVisible, String(employee.id))}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                  })
                )}
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

